import Cookies from 'js-cookie'
import omit from 'lodash/omit'
import { action, computed, observable } from 'mobx'
import qs from 'query-string'
import { v4 as uuid4 } from 'uuid'

import { adaptPostProductDraftRequest, adaptPostProductDraftResponse } from 'shared/adapters/productDrafts'
import { postProductDraft, putProductDraft } from 'shared/api/productDrafts'
import { postAsset } from 'shared/api/products'
import { RETAILER_ITEM_DRAFT, RETAILER_ITEM_NEW } from 'shared/constants/retailerItems'
import { dataURItoBlob } from 'shared/utils'

/**
 * The default config string for most Kornit products. Just a few of them require a different one.
 * @type {string}
 */
const DEFAULT_EDITOR_CONFIG = 'qmw91f527bhmp3s'

class ItemCustomizerStore {
  /** @type {import('../context').RootStore} */
  root

  mei = Math.random().toString(16).substr(2)
  meo = window.location.origin

  @observable open = false
  @observable isSaving = false
  @observable isLoaded = true
  @observable isDraft = false
  @observable error
  @observable item
  @observable id
  @observable productId
  @observable externalProductId
  @observable createdFrom
  @observable draftRef
  @observable status

  @observable useStaging = false
  @observable editorConfig = DEFAULT_EDITOR_CONFIG

  /**
   * @param {import('../context').RootStore} root
   */
  constructor (root) {
    this.root = root
    this.useStaging = !!Cookies.get('kornit_staging')
  }

  /**
   * Start designing from scratch, i.e. taking an Item as a starting point.
   * @param {Object} options params object
   * @param {string} options.productId supplier product id, should come from the `Item.uuid` field.
   * @param {string} options.externalId Kornit product ID (`Item.external_id` field).
   * @param {string} options.status current retailer product status
   * @param {string} options.customEditorConfig A custom `c` parameter for the editor <iframe> `src`.
   */
  @action.bound
  startDesigning ({ productId, externalId, status, customEditorConfig = undefined }) {
    this.productId = productId
    this.status = status
    this.externalProductId = externalId
    this.editorConfig = customEditorConfig || DEFAULT_EDITOR_CONFIG
    this.show()
  }

  /**
   * Edit an already-existing project, i.e. taking a RetailerItem as a starting point.
   * @param {Object} draft store
   * @param {string} draft.id Retailer product ID
   * @param {string} draft.productId Supplier product ID
   * @param {string} draft.createdFrom `RetailerItem.customized_item_draft_url`
   * @param {string} draft.draftRef `RetailerItem.customized_item_draft_ref`
   * @param {string} draft.status existing retailer product status
   */
  @action.bound
  editDraft ({ id, productId, status, createdFrom, draftRef }) {
    this.id = id
    this.productId = productId
    this.status = status
    this.createdFrom = createdFrom
    this.draftRef = draftRef
    this.show()
  }

  @computed get iframeOrigin () {
    return `https://${this.useStaging ? 'legacy.custom-gateway.net' : 'g3d-app.com'}`
  }

  @computed get iframeUrl () {
    const {
      useStaging,
      iframeOrigin,
      editorConfig,
      meo,
      mei,
      productId,
      externalProductId,
      draftRef,
      createdFrom,
    } = this

    if (!productId) return undefined

    const path = useStaging ? '/acp/app/' : `/s/app/wix/en_GB/${editorConfig}.html`
    const searchParams = useStaging ? { l: 'wix-staging' } : {}
    let hashParams

    if (draftRef && createdFrom) {
      const { search, hash } = new URL(createdFrom)
      Object.assign(searchParams, omit(qs.parse(search), ['p', 'mei']))
      hashParams = { ...omit(qs.parse(hash), ['p', 'mei']), ps: draftRef, mei }
    } else {
      hashParams = {
        r: '2d-canvas',
        a2c: 'postMessage',
        d: 505063,
        meo,
        mei,
        _noSavePopup: 1,
        _usePs: 1,
        p: externalProductId,
        epa: 'https://app.custom-gateway.net/api/p/3/epa-v2/:productRef/s/505063/b/any/c/USD/pt/2&d=422980',
      }
    }
    return `${iframeOrigin}${path}?${qs.stringify(searchParams)}#${qs.stringify(hashParams)}`
  }

  @action.bound
  show () {
    this.open = true
  }

  @action.bound
  hide () {
    this.open = false
  }

  @action.bound
  setIsLoaded (value) {
    this.isLoaded = Boolean(value)
  }

  @action.bound
  setIsSaving (value) {
    this.isSaving = Boolean(value)
  }

  _validateImageType (mimeType) {
    switch (mimeType) {
      case 'image/png': return 'png'
      case 'image/jpeg': return 'jpg'
      case 'image/webp': return 'webp'
      case 'image/gif': return 'gif'
      default: throw new Error('Unknown image type')
    }
  }

  async _uploadImage (dataUrl) {
    const uuid = uuid4()
    const file = dataURItoBlob(dataUrl)
    file.lastModifiedDate = new Date()
    file.name = `${uuid}.${this._validateImageType(file.type)}`
    const formData = new FormData()
    formData.append('uuid', uuid)
    formData.append('data', file, file.name)
    return postAsset(formData)
  }

  /**
   * Uploads images sequentially if data url is passed and returns a resulting array
   * of either image URLs or new asset uuids
   **/
  async _processImages (data) {
    const images = []
    for (const image of data) {
      if (/^(http|https):\/\//.test(image)) {
        images.push(image)
      } else {
        const createAssetResponse = await this._uploadImage(image)
        images.push(createAssetResponse.data.uuid)
      }
    }
    return images
  }

  /**
   * Upload images for each variant and mutate the data
   * TODO: the BE serializers will leave just a single image for a variant
   *       so we could as well do it here
   **/
  async _processVariants (data) {
    for (const variant of data) {
      variant.images = await this._processImages(variant.images)
    }
    return data
  }

  /**
   * Handles save action from POD editor
   *
   * @param {Object} options params object
   * @param {string} options.createdFrom
   * @param {string} options.draftRef
   * @param {Array} options.variants
   * @param {Array} options.sizes
   * @param {Array} options.images
   * @param {string} options.status requested status; either save to import list (`new`) or as draft
   *
   */
  @action.bound
  async saveDraft ({ createdFrom, draftRef, sizes, status = RETAILER_ITEM_NEW, ...data }) {
    this.setIsSaving(true)
    this.isDraft = status === RETAILER_ITEM_DRAFT
    this.error = undefined
    const { id, productId } = this

    const variants = await this._processVariants(data.variants)
    const images = await this._processImages(data.images)

    const payload = adaptPostProductDraftRequest({
      productId,
      createdFrom,
      draftRef,
      variants,
      sizes,
      images,
      status,
    })

    // if target status is draft and product is already added, create a new draft
    // from the same supplier product
    const apiCall = id && !(status === RETAILER_ITEM_DRAFT && this.status === RETAILER_ITEM_NEW)
      ? putProductDraft(id, payload)
      : postProductDraft(payload)

    try {
      const response = await apiCall
      this.hide()
      this.root.productDraftListStore.fetchFresh()
      this.setIsSaving(false)
      return adaptPostProductDraftResponse(response.data)
    } catch (e) {
      this.error = e?.response?.data?.error || String(e)
      this.setIsSaving(false)
    }
  }

  @action.bound
  reset () {
    this.hide()
    this.isDraft = false
    this.isLoaded = true
    this.isSaving = false
    this.id = undefined
    this.productId = undefined
    this.externalProductId = undefined
    this.createdFrom = undefined
    this.draftRef = undefined
    this.error = undefined
    this.editorConfig = DEFAULT_EDITOR_CONFIG
  }
}

export default ItemCustomizerStore
